/**
 * MachineQtyEditor.tsx
 * The "Machine Qty" cell editor for Phase-1 machine planning (2026-06-23).
 *
 * Replaces the old commit-on-blur number field with an EXPLICIT apply:
 *   - Typing only stages a pending value (no accidental change on blur).
 *   - A green ✓ "Apply" button appears when the pending value differs from the
 *     current count, so the planner SEES that the change hasn't taken effect yet.
 *   - Enter applies; the ✕ reverts to the current value.
 *
 * `liveCount` is the CURRENT per-scenario machine count (the live booking count),
 * NOT the shared persisted ScheduleTask.MachineCount — so a duplicated plan whose
 * persisted count drifted from its real bookings still edits correctly.
 *
 * `onApply` performs the update and throws on failure (e.g. not enough capable
 * machines → 409). The error is shown inline as a ⚠ with the server's message.
 */

import React, { useEffect, useState } from 'react';
import { Box, TextField, IconButton, CircularProgress, Tooltip } from '@mui/material';
import { Check, Close, Warning } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';

export interface MachineQtyEditorProps {
  /** The live current count (per-scenario booking count) to edit against. */
  liveCount: number;
  /** Apply the new count. Should throw on failure so the editor can show the error. */
  onApply: (count: number) => Promise<void>;
  /** Minimum allowed value (default 1). */
  min?: number;
}

export const MachineQtyEditor: React.FC<MachineQtyEditorProps> = ({
  liveCount,
  onApply,
  min = 1,
}) => {
  const theme = useTheme();
  const [input, setInput] = useState(String(liveCount));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-sync when the parent refetches (e.g. after a successful apply or scenario switch).
  useEffect(() => {
    setInput(String(liveCount));
    setError(null);
  }, [liveCount]);

  const parsed = parseInt(input, 10);
  const valid = Number.isFinite(parsed) && parsed >= min;
  const dirty = valid && parsed !== liveCount;

  const apply = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onApply(parsed);
      // On success the parent refetches → liveCount changes → the effect re-syncs.
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (err instanceof Error ? err.message : 'Could not change the machine count.');
      setError(msg);
      setInput(String(liveCount)); // revert the pending value on failure
    } finally {
      setSaving(false);
    }
  };

  const revert = () => {
    setInput(String(liveCount));
    setError(null);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
      <TextField
        size="small"
        type="number"
        value={input}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => { setInput(e.target.value); setError(null); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); void apply(); }
          else if (e.key === 'Escape') { revert(); }
        }}
        disabled={saving}
        inputProps={{ min, style: { textAlign: 'center', fontSize: '0.82rem', width: 36 } }}
        sx={{ width: 52 }}
      />

      {saving ? (
        <CircularProgress size={14} sx={{ ml: 0.25 }} />
      ) : dirty ? (
        <>
          <Tooltip title="Apply machine count" arrow>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); void apply(); }}
              sx={{
                p: 0.25,
                color: theme.schedule.committed,
                '&:hover': { backgroundColor: alpha(theme.schedule.committed, 0.12) },
              }}
              aria-label="Apply machine count"
            >
              <Check sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel" arrow>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); revert(); }}
              sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}
              aria-label="Cancel machine count change"
            >
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </>
      ) : error ? (
        <Tooltip title={error} arrow>
          <Warning sx={{ fontSize: 16, color: theme.schedule.conflictHard }} />
        </Tooltip>
      ) : null}
    </Box>
  );
};
